import React, { useState } from 'react';
import "./Admin.css";
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { cardApi } from '../api/card';
import Header from './Header';
import BatchCardUpload from './BatchCardUpload';

const language = "japanese";
type EncodingType = 'utf-8' | 'shift-jis';

const Admin: React.FC = () => {
  const navigate = useNavigate();

  const [encoding, setEncoding] = useState<EncodingType>('utf-8');
  const [exportImages, setExportImages] = useState(false);
  const [dataMode, setDataMode] = useState<string>('update');
  const [imageMode, setImageMode] = useState<string>('modify');
  const [file, setFile] = useState<File | null>(null);

  const handleExportCards = async () => {
    try {
      const userKey = 2;
      const token = '7e16b5527c77ea58';
      const response = await cardApi.exportCards(userKey, token, exportImages, encoding);
  
      // Create a download link for the received file
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cards_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export cards:', error);
      alert('名刺情報のエクスポート時にエラーが発生しました。');
    }
  };

  const handleImportCards = async () => {
    const userKey = 2;
    const token = '7e16b5527c77ea58';

    if (!file) {
      alert('Excelファイルを選択してください。');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const fileData = event.target?.result;
        if (!fileData) {
          alert('ファイルの読み込みに失敗しました。');
          return;
        }

        let csv = '';
        if (file.name.endsWith('.csv')) {
          csv = fileData as string;
        } else if (file.name.endsWith('.xlsx')) {
          if (typeof fileData === 'string') {
            alert('ファイルのフォーマットに誤りがあります。正しいフォーマットのファイルを選択してください。');
            return;
          }
          const workbook = XLSX.read(new Uint8Array(fileData), { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          csv = XLSX.utils.sheet_to_csv(sheet);
        } else {
          alert('ファイル形式に誤りがあります。Excel形式のファイルを選択してください。');
          return;
        }

        try {
          await cardApi.importCards(userKey, token, dataMode, imageMode, csv);
          alert('名刺情報が正しくインポートされました。');
        } catch (error) {
          console.error('Failed to import cards:', error);
          alert('ファイルのインポート時にエラーが発生しました。');
        }
      };
      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。');
      };
      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file); // for Excel files
      }
    } catch (error) {
      console.error('Error reading file:', error);
      alert('ファイルの読み込み時にエラーが発生しました。');
    }
  };

  const linkData = [
    { to: "/logs", english: "Logs", japanese: "履歴" },
//    { to: "/users", english: "Users", japanese: "ユーザー" },
//    { to: "/tags", english: "Tags", japanese: "タグ" },
  ];

  const linkElements = (
    linkData.map((link, index) => (
      <Link key={index} to={link.to}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div className="btn btn-primary" style={{ width: "100%" }}>{language === "japanese" ? link.japanese : link.english}</div>
        </div>
      </Link>
    ))
  );

  const exportElements = (
    <div className="flex flex-row mt-7 w-full space-x-2 mb-8">
      <div className="btn btn-primary" style={{ width: "100%" }} onClick={handleExportCards}>
        {language === "japanese" ? "CSVエクスポート" : "Export Card CSV"}
      </div>
      <div style={{ display: "none", justifyContent: "center", gap: '0.5rem', marginTop: "0.5rem" }}>
        <input type="checkbox" checked={exportImages} onChange={() => setExportImages(!exportImages)} />
        <span>{language === "japanese" ? "画像を含める" : "Include images"}</span>
      </div>
      <select value={encoding} onChange={(e) => setEncoding(e.target.value as EncodingType)} className='w-1/2 sm:w-fit text-sm rounded-full bg-primary-lightgray text-primary-darkgray py-2 px-4 border border-primary-bordergray'>
        <option value="utf-8">UTF-8</option>
        <option value="shift-jis">Shift-JIS</option>
      </select>
    </div>
  );

  
  const importElements = (
    <div style={{ borderTop: '0px solid #ddd', padding: '0.5rem 0' }}>
      <div className="btn btn-primary" style={{ width: "100%" }} onClick={handleImportCards}>
        {/*language === "japanese" ? "CSVインポート" : "Import Card CSV"*/}
        {language === "japanese" ? "名刺データインポート" : "Import Cards"}
      </div>

      <div style={{ marginTop: '0.5rem' }}>
        {/*<h2>{language === "japanese" ? "CSV ファイル" : "CSV file"}</h2>*/}
        <input
          type="file"
          accept=".csv, .xlsx"
          onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
        />
      </div>
      <div style={{ marginTop: '1rem', display: 'none' }}>
        <p>{language === "japanese" ? "カード データ" : "Card data"}</p>
        <select value={dataMode} onChange={(e) => setDataMode(e.target.value)} className='datum-input'>
          {/*<option value="untouched">{language === "japanese" ? "変更しない" : "Don't change"}</option>*/}
          <option value="modify">{language === "japanese" ? "新しいデータで変更する" : "Modify with new data"}</option>
          <option value="update">{language === "japanese" ? "カード全体を更新" : "Update entire cards"}</option>
          {/*<option value="replace">{language === "japanese" ? "既存のデータをすべて置き換える" : "Replace all existing data"}</option>*/}
        </select>
      </div>

      <div style={{ marginTop: '1rem', display: 'none' }}>
        <p>{language === "japanese" ? "画像データ" : "Image data"}</p>
        <select value={imageMode} onChange={(e) => setImageMode(e.target.value)} className='datum-input'>
          <option value="untouched">{language === "japanese" ? "変更しない" : "Don't change"}</option>
          <option value="modify">{language === "japanese" ? "新しいデータで変更する" : "Modify with new data"}</option>
          <option value="update">{language === "japanese" ? "カード全体を更新" : "Update entire cards"}</option>
          <option value="replace">{language === "japanese" ? "既存のデータをすべて置き換える" : "Replace all existing data"}</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="top-page-container">
      <Header />
      <div className="my-8 top-title">管理機能</div>
    
        <div className="w-full sm:w-80 flex flex-col gap-2">
          {linkElements}
          {exportElements}
          {importElements}
          <BatchCardUpload />
        </div>

    </div>
  );
};

export default Admin;
